import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { User, Camera, Save, Sun, Moon, Monitor, Loader2, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { PushNotificationsCard } from '../components/PushNotifications';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ThemeOption = ({ value, label, icon: Icon, current, onSelect }) => {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onSelect(value)}
      data-testid={`theme-option-${value}`}
      className="flex-1 flex flex-col items-center gap-2 p-4 rounded-[10px] transition-all"
      style={{
        background: active ? 'rgba(37, 99, 235, 0.12)' : 'var(--color-bg-elevated)',
        border: `1px solid ${active ? 'var(--color-blue-main)' : 'var(--color-border)'}`,
        color: active ? 'var(--color-blue-accent)' : 'var(--color-text-secondary)',
      }}
    >
      <Icon className="w-6 h-6" />
      <span className="text-sm font-medium">{label}</span>
    </button>
  );
};

const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  const { preference, setPreference } = useTheme();
  const [name, setName] = useState(user?.name || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [avatarVersion, setAvatarVersion] = useState(Date.now());
  const fileRef = useRef(null);

  const avatarUrl = user?.avatar_url
    ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}&v=${avatarVersion}`
    : null;

  const handleSaveName = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { toast.error('El nombre no puede estar vacío'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/auth/me`, { name: trimmed }, { withCredentials: true });
      await checkAuth();
      toast.success('Perfil actualizado');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Máximo 5 MB'); return; }
    if (!file.type.startsWith('image/')) { toast.error('Solo imágenes'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axios.post(`${API}/auth/me/avatar`, fd, { withCredentials: true });
      await checkAuth();
      setAvatarVersion(Date.now());
      toast.success('Foto actualizada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir foto');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    if (!user?.avatar_url) return;
    if (!window.confirm('¿Eliminar la foto de perfil?')) return;
    try {
      await axios.delete(`${API}/auth/me/avatar`, { withCredentials: true });
      await checkAuth();
      setAvatarVersion(Date.now());
      toast.success('Foto eliminada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="profile-page">
      <div>
        <h1 className="heading-1">Mi perfil</h1>
        <p className="body-text mt-1">Gestiona tu información y apariencia.</p>
      </div>

      {/* Avatar */}
      <section className="card-base p-6">
        <h2 className="heading-3 mb-4">Foto de perfil</h2>
        <div className="flex items-center gap-6">
          <div
            className="relative w-24 h-24 rounded-full flex items-center justify-center overflow-hidden"
            style={{ background: 'rgba(34, 211, 238, 0.12)', border: '1px solid var(--color-border-strong)' }}
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="avatar" className="w-full h-full object-cover" data-testid="profile-avatar" />
            ) : (
              <User className="w-10 h-10" style={{ color: 'var(--color-blue-accent)' }} />
            )}
            {uploading && (
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.55)' }}>
                <Loader2 className="w-6 h-6 text-white animate-spin" />
              </div>
            )}
          </div>
          <div className="flex-1 space-y-2">
            <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
              JPG, PNG o WEBP. Máximo 5 MB.
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="btn-secondary"
                disabled={uploading}
                data-testid="upload-avatar-btn"
              >
                <Camera className="w-4 h-4" /> {user?.avatar_url ? 'Cambiar foto' : 'Subir foto'}
              </button>
              {user?.avatar_url && (
                <button
                  type="button"
                  onClick={handleRemoveAvatar}
                  className="btn-secondary"
                  data-testid="remove-avatar-btn"
                >
                  <Trash2 className="w-4 h-4" /> Eliminar
                </button>
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
              data-testid="avatar-file-input"
            />
          </div>
        </div>
      </section>

      {/* Datos básicos */}
      <section className="card-base p-6">
        <h2 className="heading-3 mb-4">Datos personales</h2>
        <form onSubmit={handleSaveName} className="space-y-4">
          <div>
            <label className="label-text mb-2 block">Nombre</label>
            <input
              type="text"
              className="input-base"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="profile-name-input"
            />
          </div>
          <div>
            <label className="label-text mb-2 block">Email</label>
            <input
              type="text"
              className="input-base"
              value={user?.email || ''}
              readOnly
              style={{ opacity: 0.6, cursor: 'not-allowed' }}
            />
            <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>El email no se puede cambiar.</p>
          </div>
          <div>
            <label className="label-text mb-2 block">Rol</label>
            <input
              type="text"
              className="input-base"
              value={user?.role || ''}
              readOnly
              style={{ opacity: 0.6, cursor: 'not-allowed' }}
            />
          </div>
          <div className="flex justify-end">
            <button type="submit" className="btn-primary" disabled={saving} data-testid="save-profile-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Guardar cambios
            </button>
          </div>
        </form>
      </section>

      {/* Apariencia */}
      <PushNotificationsCard />
      <section className="card-base p-6">
        <h2 className="heading-3 mb-2">Apariencia</h2>
        <p className="body-text mb-4">Elige el tema visual. "Predeterminado del sistema" sigue la configuración de tu dispositivo.</p>
        <div className="flex gap-3">
          <ThemeOption value="light"  label="Claro"       icon={Sun}     current={preference} onSelect={setPreference} />
          <ThemeOption value="dark"   label="Oscuro"      icon={Moon}    current={preference} onSelect={setPreference} />
          <ThemeOption value="system" label="Sistema"     icon={Monitor} current={preference} onSelect={setPreference} />
        </div>
      </section>
    </div>
  );
};

export default ProfilePage;
